import { skipToken, useSuspenseQueries } from '@tanstack/react-query';
import React from 'react';
import { useRecoilValue } from 'recoil';

import * as api from '@gitbook/api-client';
import { assertIsDefined } from '@gitbook/util-ts';

import { useOrganizationSites } from '../../../Components';
import { apiQuery, onboardingSkippedAtom } from '../../../state';

export function useOnboardingRules(props: { organization: api.Organization }) {
    const { organization } = props;

    // Check whether the user has decided to skip onboarding (stored in local storage)
    const onboardingSkipped = useRecoilValue(onboardingSkippedAtom);

    // Check permissions
    const canCreateSiteContent = organization.permissions.createContent;

    const isInternalDocs = organization.useCase === api.OrganizationUseCase.InternalDocs;
    const [
        { data: organizationContent },
        { data: installedIntegrations },
        { data: spacesData },
        { data: membersData },
    ] = useSuspenseQueries({
        queries: [
            apiQuery('getOrganizationAllContent', { organizationId: organization.id }),
            apiQuery('listOrganizationIntegrationsStatus', {
                organizationId: organization.id,
                target: 'organization',
                limit: 100,
            }),
            apiQuery(
                'listSpacesInOrganizationById',
                isInternalDocs ? { organizationId: organization.id } : skipToken,
                { nullOnError: true }
            ),
            apiQuery(
                'listMembersInOrganizationById',
                isInternalDocs ? { organizationId: organization.id } : skipToken,
                { nullOnError: true }
            ),
        ],
    });

    assertIsDefined(installedIntegrations);

    const sitesData = useOrganizationSites(organizationContent);

    const getSiteOnboardingData = React.useCallback(() => {
        if (!canCreateSiteContent) {
            return {};
        }

        // We'll consider the site created step complete when
        // at least one site has been created
        const hasSites = sitesData.length > 0;

        // We'll consider the site linked step complete when
        // at least one site exists with a linked site space
        const hasSitesWithSpaces = sitesData.some(({ site }) => site.siteSpaces > 0);

        // We'll consider the site published step complete when
        // at least one site has been published by the org
        const hasPublishedSites = sitesData.some(({ site }) => site.published);

        return {
            hasSites,
            hasSitesWithSpaces,
            hasPublishedSites,
            site: hasSitesWithSpaces
                ? sitesData.find(({ site }) => site.siteSpaces > 0)?.site
                : hasSites
                  ? sitesData[0].site
                  : undefined,
        };
    }, [canCreateSiteContent, sitesData]);

    switch (organization.useCase) {
        case api.OrganizationUseCase.DocsSite: {
            const { site, hasSites, hasSitesWithSpaces, hasPublishedSites } =
                getSiteOnboardingData();
            const steps = [hasSites, hasSitesWithSpaces, hasPublishedSites];
            const isOnboardingComplete = steps.every(Boolean);

            // We only show the onboarding for sites if:
            //  * onboarding has not been skipped (local storage)
            //  * onboarding has not been completed (site created, site linked to space, site published)
            //  * user has site admin capabilities
            const isOnboarding = Boolean(
                !onboardingSkipped && !isOnboardingComplete && canCreateSiteContent
            );

            return {
                isOnboarding,
                steps,
                site,
            };
        }
        case api.OrganizationUseCase.AudienceControlledSite: {
            const { site, hasSites, hasSitesWithSpaces } = getSiteOnboardingData();

            const hasVASetup = sitesData.some(
                ({ site }) => site.visibility === api.SiteVisibility.VisitorAuth
            );

            const hasVASitePublished = sitesData.some(
                ({ site }) => site.visibility === api.SiteVisibility.VisitorAuth && site.published
            );

            const steps = [hasSites && hasSitesWithSpaces, hasVASetup, hasVASitePublished];
            const isOnboardingComplete = steps.every(Boolean);

            // We only show the onboarding for audience controlled sites if:
            //  * onboarding has not been skipped (local storage)
            //  * onboarding has not been completed (site created and linked, va setup, va site published)
            //  * user has site admin capabilities
            const isOnboarding = Boolean(
                !onboardingSkipped && !isOnboardingComplete && canCreateSiteContent
            );

            return {
                isOnboarding,
                steps,
                site: hasVASetup
                    ? sitesData.find(
                          ({ site }) => site.visibility === api.SiteVisibility.VisitorAuth
                      )?.site
                    : site,
            };
        }
        case api.OrganizationUseCase.InternalDocs: {
            const canAdminOrg = organization.permissions.admin;

            const hasIntegrations = installedIntegrations.items.length > 0;
            const hasInvitedMembers = membersData?.items && membersData.items.length > 1;
            const hasChangeRequests = spacesData?.items.some((space) => space.changeRequests > 0);
            const steps = [hasIntegrations, hasInvitedMembers, hasChangeRequests];
            const isOnboardingComplete = steps.every(Boolean);

            // We only show the onboarding for ikb if:
            //  * onboarding has not been skipped (local storage)
            //  * onboarding has not been completed (integration installed, more than one member of org, change request created)
            //  * user has org admin capabilities
            const isOnboarding = !onboardingSkipped && !isOnboardingComplete && canAdminOrg;

            return {
                isOnboarding,
                steps,
                space: spacesData?.items ? spacesData.items[0] : undefined,
            };
        }
        default:
            return { steps: [], isOnboarding: false };
    }
}
